import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radar } from "lucide-react";
import { api } from "../lib/api";
import { KpiRow } from "../components/dashboard/KpiRow";
import { WinRateChart, ReasonChart, BankScoreChart } from "../charts/Charts";
import { PageHeader, Panel } from "../components/common";
import { Skeleton } from "../components/ui/skeleton";
import { OpsPulse } from "../components/dashboard/OpsPulse";
import { OnboardingChecklist } from "../components/dashboard/OnboardingChecklist";
import { ActiveDisputes, InterceptsCard, StagedBanner } from "../components/dashboard/ActionCards";
import { RecentActivity } from "../components/dashboard/RecentActivity";
import { EvidenceBreakdown } from "../components/dashboard/MetricTip";
import { FirstDisputeTour } from "../components/dashboard/FirstDisputeTour";
import { RepresentmentSimulator } from "../components/RepresentmentSimulator";
import { useAuth } from "../lib/auth";

const ghost = "flex items-center gap-2 px-4 py-2.5 border border-white/15 font-mono text-xs tracking-wider hover:border-brand hover:text-brand transition-colors";

export default function CommandCenter() {
  const [stats, setStats] = useState(null);
  const [ops, setOps] = useState(null);
  const [sum, setSum] = useState(null);
  const [evOpen, setEvOpen] = useState(false);
  const { canWrite } = useAuth();
  const load = useCallback(async () => {
    const [s, o, c] = await Promise.all([api.get("/dashboard/stats"), api.get("/ops/metrics"), api.get("/command/summary")]);
    setStats(s.data); setOps(o.data); setSum(c.data);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  if (!stats || !sum) return <Skeleton className="h-96 w-full bg-[#111]" />;
  return (
    <div data-testid="command-center">
      <PageHeader eyebrow="// real-time defense" title="Command Center">
        <StagedBanner staged={sum.staged} />
      </PageHeader>
      <OnboardingChecklist />
      <div className="flex flex-wrap gap-3 mb-6" data-testid="command-actions">
        <FirstDisputeTour disputeId={sum.active[0]?.id} />
        {canWrite && <Link to="/app/risk" className={ghost} title="Score any transaction before payment" data-testid="test-transaction-btn"><Radar className="w-4 h-4" /> TEST A TRANSACTION</Link>}
        {canWrite && <RepresentmentSimulator />}
      </div>
      <KpiRow k={stats.kpis} intercepts={sum.intercepts} onEvidence={() => setEvOpen(true)} />
      {ops && <OpsPulse m={ops} />}
      <div className="grid grid-cols-12 gap-6 mt-6">
        <div className="col-span-12 xl:col-span-8"><ActiveDisputes items={sum.active} total={sum.open_total} /></div>
        <div className="col-span-12 xl:col-span-4"><InterceptsCard data={sum.intercepts} /></div>
      </div>
      <div className="grid grid-cols-12 gap-6 mt-6">
        <div className="col-span-12 xl:col-span-8 space-y-6">
          <Panel title="Dispute outcomes · monthly" testid="panel-trend"><WinRateChart data={stats.trend} /></Panel>
          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Reason code mix" testid="panel-reasons"><ReasonChart data={stats.reasons} /></Panel>
            <Panel title="Issuer win-rate scoring" testid="panel-banks"><BankScoreChart data={stats.banks} /></Panel>
          </div>
        </div>
        <div className="col-span-12 xl:col-span-4"><RecentActivity items={sum.activity} onChange={load} /></div>
      </div>
      <EvidenceBreakdown open={evOpen} onOpenChange={setEvOpen} ev={sum.evidence} />
    </div>
  );
}
